import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import {
  AdminTransactionListItemV4,
  AdminTransactionsFilterV4,
  AdminTransitionDtoV4,
} from '@models/resources/admin-transaction.model';
import {
  UpdateInvoiceRecipientDto,
  UpdateInvoiceRecipientResponse,
} from '@models/resources/transaction-request.model';

/**
 * Staff-seitige Transaktionsanfragen V4 (Sprint 6 S6-5/S6-6/S6-7/S6-8).
 *
 * Zwei konkrete Implementierungen:
 * - `DummyAdminTransactionsFacade` – teilt sich die V4-Fixtures
 *   (`DEMO_TRANSACTIONS_V4`) mit der Customer-Seite, joint sie mit
 *   denormalisierten Kundenfeldern via `TX_CUSTOMER_ID_BY_TX_ID`.
 * - `HttpAdminTransactionsFacade` – fährt gegen `GET /api/transaction-
 *   requests` (V4-BE seit Sprint 5b).
 *
 * Method-Mapping (Tech-Konzept §4 + ADR-006):
 * - `list(filter)`       → `GET /api/transaction-requests?status=&customer_id=&search=`
 * - `get(id)`            → `GET /api/transaction-requests/{id}` (Staff sieht alle)
 * - `transition(id,dto)` → `POST /api/transaction-requests/{id}/transition` (S6-6)
 * - `bulkTransition`     → `POST /api/transaction-requests/bulk-transition` (S6-7/S6-8)
 *
 * `POST /api/transaction-requests` fehlt bewusst — Anfragen werden
 * ausschließlich vom Customer erstellt (`/api/me/transaction-requests`).
 */
/**
 * Wire-Shape matched `Admin\BulkTransitionRequest` 1:1 (Sprint 5b S5b-11).
 * Sprint 6 Code-Review-Fix H-2: `transaction_ids` + `target_status`
 * statt vorherigem `transaction_request_ids` + `status` — passt jetzt
 * zum BE-Validator.
 */
export interface BulkTransitionPayloadV4 {
  readonly transaction_ids: readonly number[];
  readonly target_status: AdminTransitionDtoV4['target_status'];
  readonly comment?: string;
  /**
   * Pflicht bei Ziel-Status `depot_stored`, sonst optional. Vom S6-8-
   * Ankaufsdatum-Modal befüllt — BE rechnet pro TX `purchased_grams`
   * aus dem London-Fix vom angegebenen Tag (ADR-008 D3).
   */
  readonly purchase_date?: string;
}

/**
 * Response-Shape matched `TransactionTransitionController::bulkTransition()`.
 * Sprint 6 Code-Review-Fix H-3: `rejected[]` statt `failed[]` —
 * im BE heißt das Failure-Bucket so.
 */
export interface BulkTransitionResultV4 {
  readonly bulk_op_id?: string;
  readonly updated: readonly { id: number; status: AdminTransitionDtoV4['target_status'] }[];
  readonly rejected: readonly { id: number; reason: string }[];
}

export interface AdminTransactionsFacade {
  list(filter?: AdminTransactionsFilterV4): Observable<AdminTransactionListItemV4[]>;
  get(id: number): Observable<AdminTransactionListItemV4>;
  transition(
    id: number,
    dto: AdminTransitionDtoV4,
  ): Observable<AdminTransactionListItemV4>;
  bulkTransition(payload: BulkTransitionPayloadV4): Observable<BulkTransitionResultV4>;
  /**
   * `PATCH /api/transaction-requests/{id}/invoice-note` (Sprint 6 S6-9).
   *
   * Erlaubt im Status `consulted`. Leerer String / null setzt die
   * Notiz zurück. BE schreibt einen `invoice_note_changed`-Audit-Log.
   */
  updateInvoiceNote(id: number, invoiceNote: string | null): Observable<AdminTransactionListItemV4>;
  /**
   * `PATCH /api/invoices/{invoiceId}/recipient` (Sprint 8 U-26, BE seit
   * Sprint 7 S7-23).
   *
   * Editierbarkeit (BE enforced): `transaction`-Invoice nur vor
   * `sent_at`, `storage_fee`-Invoice jederzeit. BE wirft 422 sonst.
   *
   * Liefert den frisch resolveten Recipient-Block + die Override-
   * Felder zurück — Caller merged das in die TX-Resource, damit die
   * Card sofort up-to-date ist.
   */
  updateInvoiceRecipient(
    invoiceId: number,
    payload: UpdateInvoiceRecipientDto,
  ): Observable<UpdateInvoiceRecipientResponse>;
}

export const ADMIN_TRANSACTIONS_FACADE = new InjectionToken<AdminTransactionsFacade>(
  'AdminTransactionsFacade',
);
